/**
 * Policy Management Module (POLICY-MGMT-001, Jun 2026)
 * Admin + HR Manager: manage company policies and view compliance reporting.
 * Jul 2026 — renders inside the AdminLayout shell (standard sidebar/header),
 * so the old standalone frosted header + profile menu were removed.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { BookOpenCheck, ShieldAlert, ClipboardCheck } from 'lucide-react';
import PolicyAdminTable from './PolicyAdminTable';
import PolicyComplianceTab from './PolicyComplianceTab';
import { usePermissions } from '../../hooks/usePermissions';

const POLICY_ADMIN_ROLES = ['admin', 'hr_manager'];

export default function PolicyManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // POLICY-COMPLIANCE-REPORT-001 — policy_compliance_report:view opens the Compliance tab read-only (no admin actions)
  const { hasPermission, loading: permsLoading } = usePermissions();
  const isPolicyAdmin = POLICY_ADMIN_ROLES.includes(user?.role);
  const canViewCompliance = isPolicyAdmin || hasPermission('policy_compliance_report', 'view');
  const [tab, setTab] = useState(isPolicyAdmin ? 'policies' : 'compliance');

  React.useEffect(() => {
    document.title = 'Policy Center — TitleFlow';
    return () => { document.title = 'TitleFlow'; };
  }, []);

  if (permsLoading && !isPolicyAdmin) return null;
  if (!canViewCompliance) {
    return (
      <div className="min-h-full bg-slate-50 flex items-center justify-center py-24" data-testid="policy-access-denied">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-4">Policy Management is available to Admins and HR Managers only.</p>
          <button onClick={() => navigate('/dashboard')} className="text-blue-600 hover:underline text-sm">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50" data-testid="policy-management-page">
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Policy Management</h1>
          <p className="text-sm text-slate-500">
            Single source of truth for company policies, SOPs and compliance documents
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
          {isPolicyAdmin && <button
            onClick={() => setTab('policies')}
            data-testid="policy-tab-policies"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'policies'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <BookOpenCheck className="h-4 w-4" /> Policies
          </button>}
          <button
            onClick={() => setTab('compliance')}
            data-testid="policy-tab-compliance"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'compliance'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <ClipboardCheck className="h-4 w-4" /> Compliance
          </button>
        </div>

        {tab === 'policies' && isPolicyAdmin ? <PolicyAdminTable /> : <PolicyComplianceTab />}
      </div>
    </div>
  );
}
